import { RefObject, useEffect } from 'react';
import { useIsMdMin } from './useBreakpoints';

const FLIP_CLASS = 'is-flipped-x';
const SAFE_PADDING = 12;

export function useEdgeAwareDropdown(rootRef: RefObject<HTMLElement | null>) {
  const isDesktop = useIsMdMin();

  useEffect(() => {
    if (!isDesktop || typeof window === 'undefined') return;
    const root = rootRef.current;
    if (!root) return;

    let frame: number | null = null;

    const clearFlips = () => {
      root.querySelectorAll(`.${FLIP_CLASS}`).forEach((el) => el.classList.remove(FLIP_CLASS));
    };

    const measure = () => {
      const viewport = window.innerWidth;
      const menuRight = root.getBoundingClientRect().right;
      clearFlips();

      // 1st-level: flip when the dropdown would extend past the menu's own
      // right edge (which on this layout aligns with the nav container).
      root.querySelectorAll<HTMLElement>('.has-simple-dropdown').forEach((li) => {
        const dropdown = li.querySelector<HTMLElement>(':scope > ul.simple-dropdown');
        if (!dropdown) return;
        const liRect = li.getBoundingClientRect();
        if (liRect.left + dropdown.offsetWidth > menuRight - SAFE_PADDING) {
          li.classList.add(FLIP_CLASS);
        }
      });

      // 2nd-level: flyouts open beside the dropdown column rather than within
      // the menu, so keep the viewport as the boundary. Measure after 1st-level
      // flips so the .has-flyout <li>'s rect reflects the post-flip layout.
      root.querySelectorAll<HTMLElement>('.has-flyout').forEach((li) => {
        const flyout = li.querySelector<HTMLElement>(':scope > .flyout-dropdown');
        if (!flyout) return;
        const liRect = li.getBoundingClientRect();
        if (liRect.right + flyout.offsetWidth > viewport - SAFE_PADDING) {
          li.classList.add(FLIP_CLASS);
        }
      });
    };

    const schedule = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = null;
        measure();
      });
    };

    schedule();
    window.addEventListener('resize', schedule);

    return () => {
      window.removeEventListener('resize', schedule);
      if (frame !== null) cancelAnimationFrame(frame);
      clearFlips();
    };
  }, [isDesktop, rootRef]);
}
